import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../context/PopupContext';

const Footer = () => {
  const { t } = useTranslation();
  const { showPopup } = usePopup();
  
  return (
    <footer className="bg-neutral-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-16">
        {/* Main footer content */}
        <div className="grid grid-cols-1 gap-8 sm:gap-10">
          {/* Logo and company info - larger column */}
          <div className="space-y-4">
            <img 
              src="/bearz_logo.png"
              alt="Bearz Landscaping"
              className="h-12 sm:h-14 w-auto" 
            />
            <p className="text-neutral-400 text-sm leading-relaxed max-w-md">
              {t('footer.description')}
            </p>
            <div className="flex space-x-5 pt-2">
              <a 
                href="https://www.facebook.com/bearzlandscaping/" 
                className="text-neutral-400 hover:text-primary-400 transition-colors duration-300"
                aria-label="Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"></path>
                </svg>
              </a>
              <a 
                href="https://instagram.com" 
                className="text-neutral-400 hover:text-primary-400 transition-colors duration-300"
                aria-label="Instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"></path>
                </svg>
              </a>
            </div>
          </div>

          {/* Footer links section in grid for mobile */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 md:grid-cols-12">
            {/* Quick Links column */}
            <div className="col-span-1 sm:col-span-1 md:col-span-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-300 mb-3 sm:mb-4">
                {t('footer.quickLinks')}
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                {[
                  { path: "/", label: t('navbar.home') },
                  { path: "/services", label: t('navbar.services') },
                  { path: "/gallery", label: t('navbar.gallery') },
                  { path: "/about", label: t('navbar.about') },
                  { path: "/contact", label: t('navbar.contact') }
                ].map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.path} 
                      className="text-neutral-400 hover:text-primary-400 text-sm transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Services column */}
            <div className="col-span-1 sm:col-span-1 md:col-span-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-300 mb-3 sm:mb-4">
                {t('footer.services')}
              </h3>
              <ul className="space-y-2 sm:space-y-3">
                {[
                  { label: t('footer.servicesList.lawn') },
                  { label: t('footer.servicesList.landscaping') },
                  { label: t('footer.servicesList.hedge') },
                  { label: t('footer.servicesList.pressure') },
                  { label: t('footer.servicesList.allServices') }
                ].map((service, index) => (
                  <li key={index}>
                    <Link 
                      to="/services" 
                      className="text-neutral-400 hover:text-primary-400 text-sm transition-colors duration-200"
                    >
                      {service.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Contact Information column */}
            <div className="col-span-2 sm:col-span-1 md:col-span-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-300 mb-3 sm:mb-4">
                {t('footer.contactUs')}
              </h3>
              <ul className="space-y-3 text-neutral-400" itemScope itemType="https://schema.org/LocalBusiness">
                <meta itemProp="name" content="Bearz Landscaping" />
                <meta itemProp="url" content="https://bearzlandscaping.com" />
                <meta itemProp="areaServed" content="Montreal, West Island, Quebec" />
                <li className="flex items-start text-sm">
                  <svg className="w-5 h-5 mr-2 sm:mr-3 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                  <span itemProp="telephone">514-979-8020</span>
                </li>
                <li className="flex items-start text-sm">
                  <svg className="w-5 h-5 mr-2 sm:mr-3 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                  </svg>
                  <span className="break-all" itemProp="email">mattellerton.landscaping@gmail.com</span>
                </li>
                <li className="flex items-start text-sm">
                  <svg className="w-5 h-5 mr-2 sm:mr-3 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  <div itemProp="openingHoursSpecification" itemScope itemType="https://schema.org/OpeningHoursSpecification">
                    <meta itemProp="dayOfWeek" content="Monday Tuesday Wednesday Thursday Friday" />
                    <meta itemProp="opens" content="09:00" />
                    <meta itemProp="closes" content="17:00" />
                    <p>{t('footer.hours.weekdays')}: <span itemProp="hoursAvailable">9:00 AM - 5:00 PM</span></p>
                    <p>{t('footer.hours.saturday')}: {t('footer.hours.closed')}</p>
                    <p>{t('footer.hours.sunday')}: {t('footer.hours.closed')}</p>
                  </div>
                </li>
                <li className="mt-4 text-sm">
                  <span className="font-medium">{t('footer.servicedAreas.title')}</span> 
                  <p className="mt-1">{t('footer.servicedAreas.list')}</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Call to action section */}
        <div className="mt-8 sm:mt-10 md:mt-12 pt-6 border-t border-neutral-800 flex flex-col sm:flex-row justify-between items-center">
          <div className="text-neutral-500 text-xs sm:text-sm text-center sm:text-left">
            <p>&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
            <p className="mt-1">{t('footer.tagline')}</p>
            <p className="mt-1 text-xs">
              Powered by <button 
                onClick={showPopup} 
                className="text-primary-400 hover:text-primary-300 transition-colors bg-transparent border-none p-0 inline cursor-pointer focus:outline-none focus:underline"
              >
                Netfluence
              </button>
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link 
              to="/contact"
              className="inline-block bg-primary-600 hover:bg-primary-500 text-white font-medium px-4 sm:px-5 md:px-6 py-2 rounded text-sm transition-colors duration-200"
            >
              {t('footer.requestQuote')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 